import {Checkout, CheckoutFactory, CountriesFactory, IAddressData} from "@connox/typescript-library";
import {ICheckoutPutParams} from "@connox/typescript-library/types/checkout/requestHandlers/CheckoutRequestHandler";
import braintree, {PayPalCheckout} from "braintree-web";
import * as React from "react";
import {useCartContext} from "../../cart/CartContextProvider/CartContextProvider";
import {getShopId} from "../getShopId";
import window from "../WindowExtended";
import {PaymentController, Payments, isoLocales, INTERNAL_ERROR_TYPE} from "./PaymentController";
import {getLineItems, payPalButtonStyle} from "./PaypalModule";
import {isToNorthIreland} from "../../checkout/steps/AddressStep/AddressStep";
import {Environment, FlowType, Intent} from "paypal-checkout-components";
import {IPaypal} from "./PaypalModule";

export const PayPalExpressButton: React.FC<{onReturn: () => void; handleError: (err: Error) => void}> = (props) => {
  const {cart} = useCartContext();
  const payPalButton = React.useRef<HTMLDivElement>(null);

  React.useEffect(() => {
    if (!payPalButton.current) {
      return;
    }

    const locale = isoLocales[getShopId()];
    let instance: null | PayPalCheckout = null;
    let checkout: Checkout | null = null;

    import("paypal-checkout")
      .then((paypal: IPaypal) =>
        paypal.Button.render(
          {
            env: (__DEVELOPMENT__ ? "sandbox" : "production") as Environment,
            locale,
            onAuthorize: async (data) => {
              /* AFTER COMPLETING PAYPAL CHECKOUT AND POPUP CLOSING */

              if (instance === null || checkout === null) {
                throw new Error("Could not create PayPalCheckout instance");
              }
              const tokenized = await instance.tokenizePayment(data);

              // eslint-disable-next-line camelcase
              window.pp_nonce = tokenized.nonce;
              if (!tokenized.details.shippingAddress) {
                throw new Error("PayPal hat uns leider keine oder eine ungültige Lieferadresse übergeben");
              }
              const {city, countryCode, line1, postalCode, recipientName} = tokenized.details.shippingAddress;
              const allCountries = await CountriesFactory.getAllCountries(getShopId());
              if (!allCountries.find((country) => country.countryCode === countryCode)) {
                throw new Error("Das ausgewählte Lieferland kann leider nicht beliefert werden");
              }
              if (isToNorthIreland(countryCode, postalCode)) {
                throw new Error("Die ausgewählte Adresse kann leider nicht beliefert werden");
              }
              const streetAndNum = splitStreetAndNumber(line1, countryCode);
              const recipientNameArray = recipientName?.split(" ") ?? ["", ""];
              const street = streetAndNum[0];
              const streetNumber = streetAndNum[1];
              const lastName = recipientNameArray.pop();
              const firstName = recipientNameArray.join(" ");

              const address: Partial<IAddressData> = {
                city,
                countryCode,
                email: tokenized.details.email,
                firstName,
                gender: "M",
                lastName,
                number: streetNumber,
                street,
                zip: postalCode,
              };
              await checkout.put({
                address: {
                  delivery: address,
                  invoice: address,
                },
                logisticsTypeId: cart.logisticsTypeId,
              } as ICheckoutPutParams);

              props.onReturn();

              return tokenized;

              /*/ region connox payment
            const response = await PaymentFactory.authorizePayment(payment.paymentTransactionId, nonce);
            // endregion connox payment */
            },
            onCancel: async () => {
              checkout = await CheckoutFactory.generateCheckout(getShopId(), cart);

              await checkout.put({paymentId: null});
            },
            onError: (err) => {
              // Don't ask why, but this is a string in braintree paypal type
              // definitions and an Error instance in production environment
              const tempError = typeof err === "string" ? new Error(err) : (err as unknown as Error);
              tempError.name = INTERNAL_ERROR_TYPE.PP_EXPRESS.OnError + tempError.name;
              props.handleError(tempError);
            },
            payment: async () => {
              /* BEFORE OPENING POPUP */
              /*
               * I'm not using checkoutContext on purpose making sure to only generate a checkout when button is
               * actually clicked.
               */
              checkout = await CheckoutFactory.generateCheckout(getShopId(), cart);

              await checkout.put({paymentId: Payments.PaypalExpressBraintree});
              PaymentController.createPayment(checkout, Payments.PaypalExpressBraintree);
              const {client} = await PaymentController.initBraintreeClient();
              instance = await braintree.paypalCheckout.create({client});

              if (instance === null) {
                throw new Error("Could not create PayPalCheckout instance");
              }

              return await instance.createPayment({
                amount: checkout.cartData.value.payable.amount,
                currency: checkout.cartData.currencyCode,
                displayName: "connox - be unique",
                enableShippingAddress: true,
                flow: "checkout" as FlowType,
                intent: "authorize" as Intent,
                lineItems: getLineItems(checkout.cartData),
                locale,
                shippingAddressEditable: true,
              });
            },
            style: payPalButtonStyle,
          },
          "#PaypalExpressButton",
        ),
      )
      .catch((err: Error) => {
        if (err.name.startsWith(INTERNAL_ERROR_TYPE.PP_EXPRESS.OnError)) return;
        err.name = INTERNAL_ERROR_TYPE.PP_EXPRESS.OnCatch + err.name;
        props.handleError(err);
      });
  }, []);

  return <div id="PaypalExpressButton" ref={payPalButton} />;
};

export default PayPalExpressButton;

/* Just split address line dependent on countryCode:
 * -default: by space before the first word that contains decimals
 * -GB/FR: reverse order, split by space after word with first decimal appearance
 * -US: shift out first value as street number, the remains as street
 */
function splitStreetAndNumber(line: string, countryCode: string): string[] {
  if (countryCode === "US") {
    const streetLine = line.split(" ");
    const usNumber = streetLine.shift() ?? "";
    const usStreet = streetLine.join(" ");

    return [usStreet, usNumber];
  }

  const swapStreetAndNum = ["GB", "FR"].includes(countryCode);

  const tempLine = swapStreetAndNum ? line.split("").reverse().join("") : line;

  const firstDecimalAppearance = tempLine.search(/\d/);
  const whitespaceAppearanceBeforeDecimal =
    firstDecimalAppearance - tempLine.substr(0, firstDecimalAppearance).split("").reverse().join("").search(/\s/);

  const tempStreet = tempLine.substr(0, whitespaceAppearanceBeforeDecimal - 1);
  const tempNo = tempLine.substr(whitespaceAppearanceBeforeDecimal);

  const street = swapStreetAndNum ? tempStreet.split("").reverse().join("") : tempStreet;
  const streetNo = swapStreetAndNum ? tempNo.split("").reverse().join("") : tempNo;

  return [street, streetNo];
}
